/**
 * The screen's one primary action, declared by the route it belongs to.
 *
 * The action sat in the shell's own chrome, so every screen's primary action
 * had to be listed there, in a component that knows nothing about any of them.
 * A route naming its own action keeps that knowledge with the screen.
 *
 * @module components/shell/RouteAction
 */

import React, { type ComponentType } from "react";

import { useMatches } from "react-router";

/** What a route may put in its `handle` to claim the top bar's action slot. */
export interface RouteActionHandle {
  /**
   * Rendered with no props. It reads whatever it needs from context, which is
   * how an action gated on a permission stays gated.
   */
  action?: ComponentType;
}

/**
 * @example
 * ```tsx
 * { path: "programmes", handle: { action: NewProgrammeAction }, element: <ProgrammesRoute /> }
 * ```
 */
export function RouteAction(): React.ReactElement | null {
  const matches = useMatches();
  // Deepest first: a child route's action replaces its parent's rather than
  // joining it, since the slot holds one.
  const owner = [...matches]
    .reverse()
    .find((match) => (match.handle as RouteActionHandle | undefined)?.action);
  const Action = (owner?.handle as RouteActionHandle | undefined)?.action;
  return Action ? <Action /> : null;
}
